import { createContext, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, Stack, Typography } from "@mui/material";
import {
  isValidToken,
  setSession,
  setSuperSession,
  setOrganisationSession,
  setOrganisationName,
  setOrganisationAdmin,
  setImpersonateSession,
  getTokenExp,
  isSuperAdmin,
} from "../utils/jwt";

import axios from "../utils/axios/v1/userAxios";
import {
  getEffectiveFeaturesSnapshot,
  hasLoadedEffectiveFeatures,
  isFeatureEnabledV2,
} from "./user/EffectiveFeaturesContext";

import { UserType, normalizeUserType } from "../utils/constants";
import colors from "../utils/colors";

// Licensing v2 shim - legacy integer FeatureId.X -> string key.
// The shim in isFeatureEnabled/getFeatureValue below routes legacy
// callers through this table so existing call sites keep working.
const FEATURE_ID_TO_KEY = {
  1: "platform.sites",
  2: "waf.signature.owasp",
  3: "web.certs.wildcard",
  4: "platform.requests",
  5: "platform.data_retention",
  7: "web.certs.custom",
  8: "waf.rules.custom",
  9: "waf.block_page.custom",
  10: "platform.team_management",
  11: "platform.rbac",
  12: "web.rate_limit",
  13: "web.rate_limit.advanced",
  14: "platform.log_payload",
  15: "waf.signature.layerd",
  16: "platform.b2b_saml",
  17: "bot_management",
  18: "platform.cache",
  19: "waf.smart_parse",
};

const INITIALIZE = "INITIALIZE";
const SIGN_IN = "SIGN_IN";
const SIGN_OUT = "SIGN_OUT";
const SIGN_UP = "SIGN_UP";
const SET_USER = "SET_USER";
const SET_FEATURES = "SET_FEATURES";
const SET_NOTIFICATIONS = "SET_NOTIFICATIONS";
const SET_SUPER_USER = "SET_SUPER_USER";
const SET_USER_TYPE = "SET_USER_TYPE";
const SET_ADMIN_USER_TYPE = "SET_ADMIN_USER_TYPE";
const SET_USER_LICENSE = "SET_USER_LICENSE";
const HOME_CONTROLLER = "HOME_CONTROLLER";
const WAFDASH_CONTROLLER = "WAFDASH_CONTROLLER";
const LLMDASH_CONTROLLER = "LLMDASH_CONTROLLER";
const WEBSITE_CONTROLLER = "WEBSITE_CONTROLLER";
const ZONE_CONTROLLER = "ZONE_CONTROLLER";
const WAFEVENT_CONTROLLER = "WAFEVENT_CONTROLLER";
const LLMEVENT_CONTROLLER = "LLMEVENT_CONTROLLER";
const PLAN_CONTROLLER = "PLAN_CONTROLLER";
const SET_USER_ID = "SET_USER_ID";
const GET_ORGANISATION = "GET_ORGANISATION";
const SET_TOKEN_REFRESH_TIMER = "SET_TOKEN_REFRESH_TIMER";
const SET_USER_PERMISSIONS = "SET_USER_PERMISSIONS";

const SET_ERROR = "SET_ERROR";
const INVALID_USER_TYPE = null;

const resolveUserType = (user) => normalizeUserType(user?.userType ?? user?.role);
const isUnauthorizedError = (err) => err?.response?.status === 401 || err?.message === "Unauthorized";

function mergeUserPermissions(user) {
  if (!user) return [];
  const rolePermissions = user?.role?.permissions || [];
  const projectPermissions = user?.projects?.flatMap((p) => p.permissions || []) || [];
  return [...new Set([...rolePermissions, ...projectPermissions])];
}

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  sauser: null,
  userType: INVALID_USER_TYPE,
  adminUserType: INVALID_USER_TYPE,
  userLicense: -1,
  features: null,
  notifications: null,
  permissions: [],
  organisation: null,
  homeController: null,
  wafdashController: null,
  llmdashController: null,
  websiteController: null,
  zoneController: null,
  wafeventController: null,
  llmeventController: null,
  planController: null,
  user_id: null,
  errMsg: null,
  tokenRefreshTimer: null,
};

const JWTReducer = (state, action) => {
  switch (action.type) {
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    case INITIALIZE:
      return {
        ...state,
        isInitialized: true,
        isAuthenticated: action.payload.isAuthenticated,
        user: action.payload.user,
        sauser: action.payload.sauser,
        ...(action.payload.permissions !== undefined
          ? { permissions: action.payload.permissions }
          : {}),
      };
    case SET_SUPER_USER:
      return {
        ...state,
        isAuthenticated: true,
        sauser: action.payload.sauser,
      };
    case SET_USER_TYPE:
      return {
        ...state,
        userType: action.payload.userType,
      };
    case SET_ADMIN_USER_TYPE:
      return {
        ...state,
        adminUserType: action.payload.adminUserType,
      };
    case SET_USER_LICENSE:
      return {
        ...state,
        userLicense: action.payload.userLicense,
      };
    case SET_USER:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      };
    case SET_FEATURES:
      return {
        ...state,
        features: action.payload.features,
      };
    case SET_NOTIFICATIONS:
      return {
        ...state,
        notifications: action.payload.notifications,
      };
    case SIGN_IN:
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        sauser: action.payload.sauser,
      };
    case SIGN_OUT:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        sauser: null,
        userType: INVALID_USER_TYPE,
        userLicense: -1,
      };

    case SIGN_UP:
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        sauser: null,
        userType: INVALID_USER_TYPE,
        userLicense: -1,
      };
    case HOME_CONTROLLER:
      return {
        ...state,
        homeController: action.payload.homeController,
      };
    case WAFDASH_CONTROLLER:
      return {
        ...state,
        wafdashController: action.payload.wafdashController,
      };
    case LLMDASH_CONTROLLER:
      return {
        ...state,
        llmdashController: action.payload.llmdashController,
      };
    case WEBSITE_CONTROLLER:
      return {
        ...state,
        websiteController: action.payload.websiteController,
      };
    case ZONE_CONTROLLER:
      return {
        ...state,
        zoneController: action.payload.zoneController,
      };
    case WAFEVENT_CONTROLLER:
      return {
        ...state,
        wafeventController: action.payload.wafeventController,
      };
    case LLMEVENT_CONTROLLER:
      return {
        ...state,
        llmeventController: action.payload.llmeventController,
      };
    case PLAN_CONTROLLER:
      return {
        ...state,
        planController: action.payload.planController,
      };
    case SET_USER_ID:
      return {
        ...state,
        user_id: action.payload.user_id,
      };
    case GET_ORGANISATION:
      return {
        ...state,
        organisation: action.payload.organisation,
      };
    case SET_TOKEN_REFRESH_TIMER:
      return {
        ...state,
        tokenRefreshTimer: action.payload.tokenRefreshTimer,
      };
    case SET_USER_PERMISSIONS:
      return {
        ...state,
        permissions: action.payload.permissions,
      };
    default:
      return state;
  }
};

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();

  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const [auth0TokenTimer, setAuth0TokenTimer] = useState(null);
  const unauthorizedLockRef = useRef(false);

  const setUserType = useCallback((userType) => {
    dispatch({
      type: SET_USER_TYPE,
      payload: {
        userType,
      },
    });
  }, []);
  const onAuth0AccessTokenUpdated = useCallback((accessToken) => {
    const exp = getTokenExp(accessToken);
    const now = Date.now();
    if (exp < now / 1000) {
      logout({ returnTo: window?.location?.origin + "/v2/overview" });
      return;
    }
    if (auth0TokenTimer) {
      clearTimeout(auth0TokenTimer);
    }
    setAuth0TokenTimer(
      setTimeout(() => {
        logout({ returnTo: window?.location?.origin + "/v2/overview" });
      }, exp * 1000 - now)
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const handleUnauthorized = useCallback(() => {
    if (unauthorizedLockRef.current) return;
    unauthorizedLockRef.current = true;
    setSession(null);
    setSuperSession(null);
    setOrganisationSession(null);
    setImpersonateSession(null);
    dispatch({
      type: INITIALIZE,
      payload: {
        isAuthenticated: false,
        user: null,
        sauser: null,
        permissions: [],
      },
    });
    setUserType(INVALID_USER_TYPE);
    setErr("Unauthorized");
  }, [setErr, setUserType]);

  useEffect(() => {
    if (isAuthenticated && !unauthorizedLockRef.current) {
      const setAccessToken = async () => {
        try {
          const accessToken = await getAccessTokenSilently();
          onAuth0AccessTokenUpdated(accessToken);

          setSession(accessToken);

          const response = await axios.get("/auth");
          const user = response.data;

          if (isSuperAdmin(resolveUserType(user))) {
            setSession(null);
            setSuperSession(accessToken);
          }
        } catch (err) {
          if (isUnauthorizedError(err)) {
            handleUnauthorized();
            return;
          }
          setErr(err?.response?.data?.message || err.message);
        }
      };
      setAccessToken().catch(() => undefined);
    }
  }, [isAuthenticated, getAccessTokenSilently, onAuth0AccessTokenUpdated, handleUnauthorized, setErr]);

  const setUserId = useCallback((msg) => {
    dispatch({
      type: SET_USER_ID,
      payload: {
        user_id: msg,
      },
    });
  }, []);

  /*
    const refreshUserInfo = useCallback(async () => {
        const accessToken = await getAccessTokenSilently();
        onAuth0AccessTokenUpdated(accessToken);
        setSession(accessToken);
        await axios.post("/auth/refresh-user");
    }, [getAccessTokenSilently, onAuth0AccessTokenUpdated]);
    */
  const getFeatures = useCallback(async () => {
    try {
      const response = await axios.get("/auth/features");
      const features = response.data;
      dispatch({
        type: SET_FEATURES,
        payload: {
          features,
        },
      });
    } catch (err) {
      dispatch({
        type: SET_FEATURES,
        payload: {
          features: null,
        },
      });
      setErr(err.message);
    }
  }, [setErr]);

  const setAccessToken = useCallback(async () => {
    if (unauthorizedLockRef.current) {
      return false;
    }
    try {
      const accessToken = await getAccessTokenSilently();
      onAuth0AccessTokenUpdated(accessToken);

      setSession(accessToken);

      const response = await axios.get("/auth");
      const user = response.data;
      
      dispatch({
        type: SET_USER_PERMISSIONS,
        payload: {
          permissions: mergeUserPermissions(user),
        },
      });

      if (isSuperAdmin(resolveUserType(user))) {
        setSession(null);
        setSuperSession(accessToken);
        dispatch({
          type: SET_SUPER_USER,
          payload: {
            sauser: user,
          },
        });
        dispatch({
          type: SET_USER_LICENSE,
          payload: {
            userLicense: user?.organisation?.license,
          },
        });
        dispatch({
          type: SET_ADMIN_USER_TYPE,
          payload: {
            adminUserType: resolveUserType(user),
          },
        });
        setUserType(UserType.ORG_ADMIN);
      } else {
        dispatch({
          type: SET_USER,
          payload: {
            user: user,
          },
        });
        setUserType(resolveUserType(user));
        dispatch({
          type: SET_USER_LICENSE,
          payload: {
            userLicense: user?.organisation?.license,
          },
        });
        await getFeatures();
      }
      return true;
    } catch (err) {
      if (isUnauthorizedError(err)) {
        handleUnauthorized();
      } else {
        setErr(err?.response?.data?.message || err.message);
      }
      return false;
    }
  }, [getAccessTokenSilently, getFeatures, onAuth0AccessTokenUpdated, setUserType, handleUnauthorized, setErr]);

  const clearFeatures = useCallback(() => {
    dispatch({
      type: SET_FEATURES,
      payload: {
        features: null,
      },
    });
  }, []);

  const isFeatureEnabled = useCallback(
    (feature_id) => {
      // Licensing v2 shim - legacy FeatureId.X -> string key -> effective map.
      const key = FEATURE_ID_TO_KEY[feature_id];
      if (!key) return false;
      const map = getEffectiveFeaturesSnapshot();
      return isFeatureEnabledV2(map, key);
    },
    []
  );

  const getFeatureValue = useCallback(
    (feature_id) => {
      const key = FEATURE_ID_TO_KEY[feature_id];
      if (!key) return null;
      const map = getEffectiveFeaturesSnapshot();
      const v = map ? map[key] : undefined;
      return v === undefined ? null : v;
    },
    []
  );

  useEffect(() => {
    const initialize = async () => {
      let hydratedUser = null;
      let hydratedSauser = null;
      let isAuthenticated = false;

      try {
        const accessToken = window.localStorage.getItem("accessToken");
        const accessSuperToken = window.localStorage.getItem("accessSuperToken");
        setOrganisationSession(window.localStorage.getItem("accessOrganisationToken"));
        setImpersonateSession(window.localStorage.getItem("accessImpersonateToken"));
        setOrganisationName(window.localStorage.getItem("OrgName"));
        setOrganisationAdmin(window.localStorage.getItem("OrgAdmin"));

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const response = await axios.get("/auth");
          const user = response.data;
          hydratedUser = user;
          isAuthenticated = true;
          dispatch({
            type: SET_USER,
            payload: {
              user: user,
            },
          });
          dispatch({
            type: SET_USER_PERMISSIONS,
            payload: {
              permissions: mergeUserPermissions(user),
            },
          });
          setUserType(resolveUserType(user));
          dispatch({
            type: SET_USER_LICENSE,
            payload: {
              userLicense: user?.organisation?.license,
            },
          });
          await getFeatures();
        } else {
          dispatch({
            type: SET_USER,
            payload: {
              user: null,
            },
          });
        }
        if (accessSuperToken && isValidToken(accessSuperToken)) {
          setSuperSession(accessSuperToken);
          const response = await axios.get("/auth");
          const user = response.data;
          hydratedSauser = user;
          isAuthenticated = true;
          dispatch({
            type: SET_SUPER_USER,
            payload: {
              sauser: user,
            },
          });
          dispatch({
            type: SET_USER_PERMISSIONS,
            payload: {
              permissions: mergeUserPermissions(user),
            },
          });
          dispatch({
            type: SET_USER_LICENSE,
            payload: {
              userLicense: user?.organisation?.license,
            },
          });
          dispatch({
            type: SET_ADMIN_USER_TYPE,
            payload: {
              adminUserType: resolveUserType(user),
            },
          });
          setUserType(UserType.ORG_ADMIN);
        } else {
          dispatch({
            type: SET_SUPER_USER,
            payload: {
              sauser: null,
            },
          });
        }

        const mergedPermissions = mergeUserPermissions(hydratedUser || hydratedSauser);
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated,
            user: hydratedUser,
            sauser: hydratedSauser,
            permissions: mergedPermissions,
          },
        });
      } catch (err) {
        if (isUnauthorizedError(err)) {
          handleUnauthorized();
          return;
        }
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null,
            sauser: null,
            permissions: [],
          },
        });
        setUserType(INVALID_USER_TYPE);
        dispatch({
          type: SET_USER_LICENSE,
          payload: {
            userLicense: -1,
          },
        });
        setErr(err?.response?.data?.message || err.message);
      }
    };

    initialize();
  }, [getFeatures, setUserType, handleUnauthorized, setErr]);
  const getUser = useCallback(async () => {
    try {
      const response = await axios.get("/auth");
      const user = response.data;
      
      dispatch({
        type: SET_USER_PERMISSIONS,
        payload: {
          permissions: mergeUserPermissions(user),
        },
      });

      if (isSuperAdmin(resolveUserType(user))) {
        dispatch({
          type: SET_ADMIN_USER_TYPE,
          payload: {
            adminUserType: resolveUserType(user),
          },
        });
        dispatch({
          type: SET_USER_LICENSE,
          payload: {
            userLicense: user?.organisation?.license,
          },
        });
        setUserType(UserType.ORG_ADMIN);
      } else {
        setUserType(resolveUserType(user));
        dispatch({
          type: SET_USER_LICENSE,
          payload: {
            userLicense: user?.organisation?.license,
          },
        });
      }
    } catch (err) {
      dispatch({
        type: INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null,
          sauser: null,
        },
      });
    }
  }, [setUserType]);

  const signIn = useCallback(
    async (values) => {
      unauthorizedLockRef.current = false;
      const response = await axios.post("/auth/login", values);
      const user = response.data;
      if (values.remember) {
        localStorage.setItem("email", values.email);
        localStorage.setItem("remember", true);
      } else {
        localStorage.removeItem("email");
        localStorage.removeItem("remember");
      }
      if (isSuperAdmin(resolveUserType(user))) {
        setSuperSession(user?.jwtToken);
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: true,
            user: null,
            sauser: user,
          },
        });
        dispatch({
          type: SET_ADMIN_USER_TYPE,
          payload: {
            adminUserType: resolveUserType(user),
          },
        });
        return "SA";
      } else {
        setSession(user?.jwtToken);
        dispatch({
          type: INITIALIZE,
          payload: {
            isAuthenticated: true,
            user: user,
            sauser: null,
          },
        });
        setUserType(resolveUserType(user));
        dispatch({
          type: SET_USER_LICENSE,
          payload: {
            userLicense: user?.organisation?.license,
          },
        });
        return "NSA";
      }
    },
    [setUserType]
  );

  const signOut = useCallback(async () => {
    unauthorizedLockRef.current = false;
    if (auth0TokenTimer) {
      clearTimeout(auth0TokenTimer);
      setAuth0TokenTimer(null);
    }
    await logout({ federated: true });
    setSession(null);
    setSuperSession(null);
    setOrganisationSession(null);
    setImpersonateSession(null);
    setOrganisationName(null);
    setOrganisationAdmin(null);
    dispatch({ type: SET_FEATURES, payload: { features: null } });
    dispatch({ type: SIGN_OUT });
  }, [logout, auth0TokenTimer]);

  useEffect(() => {
    window.__layerdSignOut = signOut;
    return () => {
      window.__layerdSignOut = null;
    };
  }, [signOut]);

  const signUp = useCallback(async (values) => {
    const response = await axios.post("/auth/register", values);
    return response.data;
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const response = await axios.get("/auth/verify-email?token=" + token);
    return response.data;
  }, []);

  const forgetPassword = useCallback(async (email) => {
    const response = await axios.post("/auth/forgot-password", {
      email: email,
    });
    return response.data;
  }, []);

  const resetPassword = useCallback(async (values, token) => {
    values.token = token.token;
    const response = await axios.post("/auth/reset-password", values);
    return response.data;
  }, []);
  const updateProfile = useCallback(async (values) => {
    const response = await axios.patch("/auth", values);
    const user = response.data;
    if (isSuperAdmin(resolveUserType(user))) {
      dispatch({
        type: SET_SUPER_USER,
        payload: {
          sauser: user,
        },
      });
      dispatch({
        type: SET_ADMIN_USER_TYPE,
        payload: {
          adminUserType: resolveUserType(user),
        },
      });
    } else {
      dispatch({
        type: SET_USER,
        payload: {
          user: user,
        },
      });
    }
    if (response && response.data) {
      return true;
    } else {
      return false;
    }
  }, []);

  const getOrganisation = useCallback(async () => {
    try {
      const response = await axios.get("/organisation");
      dispatch({
        type: GET_ORGANISATION,
        payload: {
          organisation: response.data,
        },
      });
    } catch (err) {
      setErr(err.message);
    }
  }, [setErr]);

  const updateOrganisation = useCallback(
    async (values) => {
      try {
        const response = await axios.patch("/organisation", values);
        dispatch({
          type: GET_ORGANISATION,
          payload: {
            organisation: response.data,
          },
        });
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr]
  );
  const setHomeController = useCallback((controller) => {
    dispatch({
      type: HOME_CONTROLLER,
      payload: {
        homeController: controller,
      },
    });
  }, []);
  const setWafDashController = useCallback((controller) => {
    dispatch({
      type: WAFDASH_CONTROLLER,
      payload: {
        wafdashController: controller,
      },
    });
  }, []);
  const setLLMDashController = useCallback((controller) => {
    dispatch({
      type: LLMDASH_CONTROLLER,
      payload: {
        llmdashController: controller,
      },
    });
  }, []);
  const setWebsiteController = useCallback((controller) => {
    dispatch({
      type: WEBSITE_CONTROLLER,
      payload: {
        websiteController: controller,
      },
    });
  }, []);
  const setZoneController = useCallback((controller) => {
    dispatch({
      type: ZONE_CONTROLLER,
      payload: {
        zoneController: controller,
      },
    });
  }, []);
  const setWafEventController = useCallback((controller) => {
    dispatch({
      type: WAFEVENT_CONTROLLER,
      payload: {
        wafeventController: controller,
      },
    });
  }, []);
  const setLLMEventController = useCallback((controller) => {
    dispatch({
      type: LLMEVENT_CONTROLLER,
      payload: {
        llmeventController: controller,
      },
    });
  }, []);
  const setPlanController = useCallback((controller) => {
    dispatch({
      type: PLAN_CONTROLLER,
      payload: {
        planController: controller,
      },
    });
  }, []);

  const setUserLicense = useCallback((license) => {
    dispatch({
      type: SET_USER_LICENSE,
      payload: {
        userLicense: license,
      },
    });
  }, []);
  const setImpersonate = useCallback(
    async (user) => {
      setImpersonateSession(user?.jwtToken);
      dispatch({
        type: SET_USER,
        payload: {
          user: user,
        },
      });
      setUserType(resolveUserType(user));
      dispatch({
        type: SET_USER_LICENSE,
        payload: {
          userLicense: user?.organisation?.license,
        },
      });
    },
    [setUserType]
  );
  const finishImpersonate = useCallback(() => {
    setImpersonateSession(null);

    dispatch({
      type: SET_USER,
      payload: {
        user: null,
      },
    });
    setUserType(INVALID_USER_TYPE);
    dispatch({
      type: SET_USER_LICENSE,
      payload: {
        userLicense: -1,
      },
    });
    clearFeatures();
  }, [setUserType, clearFeatures]);
  const getNotifications4User = useCallback(async () => {
    try {
      const response = await axios.get("notification");
      dispatch({
        type: SET_NOTIFICATIONS,
        payload: {
          notifications: response.data,
        },
      });
    } catch (err) {
      setErr(err.message);
      dispatch({
        type: SET_NOTIFICATIONS,
        payload: {
          notifications: [],
        },
      });
    }
  }, [setErr]);
  const markNotificationAsRead = useCallback(
    async (id) => {
      try {
        await axios.patch(`notification/mark_read/${id}`);
        await getNotifications4User();
      } catch (err) {
        setErr(err.message);
      }
    },
    [setErr, getNotifications4User]
  );
  const markAllNotificationAsRead = useCallback(async () => {
    try {
      await axios.patch("notification/mark_read");
      await getNotifications4User();
    } catch (err) {
      setErr(err.message);
    }
  }, [setErr, getNotifications4User]);

  const resendVerificationEmail = useCallback(async (user_id) => {
    try {
      const response = await axios.post("auth/resend-email", { user_id });
      return response.data;
    } catch (err) {
      return err.message;
    }
  }, []);

  if (state.errMsg === "Unauthorized") {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 4, bgcolor: colors.neutral[100] }}>
        <Stack
          spacing={2}
          sx={{ maxWidth: 520, textAlign: "center", bgcolor: "#fff", border: `1px solid ${colors.neutral[200]}`, borderRadius: "10px", p: 4 }}
        >
          <Typography sx={ { fontSize: 24, fontWeight: "medium", color: colors.neutral[1000] }}>
            Session expired
          </Typography>
          <Typography sx={ { fontSize: 14, color: colors.neutral[700] }}>
            Unauthorized. Please sign in again to continue.
          </Typography>
          <Button
            variant="contained"
            onClick={() => logout({ returnTo: window?.location?.origin + "/v2/overview" })}
            sx={{ textTransform: "none", alignSelf: "center" }}
          >
            Go to login
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        getUser,
        getFeatures,
        isFeatureEnabled,
        getFeatureValue,
        clearFeatures,
        signIn,
        signOut,
        signUp,
        verifyEmail,
        forgetPassword,
        updateProfile,
        resetPassword,
        setHomeController,
        setWafDashController,
        setLLMDashController,
        setWebsiteController,
        setZoneController,
        setWafEventController,
        setLLMEventController,
        setPlanController,
        setUserType,
        setUserLicense,
        setImpersonate,
        finishImpersonate,
        getNotifications4User,
        markNotificationAsRead,
        markAllNotificationAsRead,
        setAccessToken,
        // refreshUserInfo,
        resendVerificationEmail,
        setUserId,
        getOrganisation,
        updateOrganisation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
